import React from 'react';

import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';

import {
    useChrono,
    useCurrentTracklog,
    useDebriefApp,
    useDebriefSegments,
    useAnalytics,
    useCurrentAircraft,
    useFlightDataDirectory,
} from 'ff/debrief/hooks';
import { AxisId } from 'ff/debrief/models';

import { getDownSampler, ColorBadge } from './utils';
import { AxisSelectField, AxisSelectForm, axisConfig } from './axis';
import FlightChart from './FlightChart';
import { SegmentMarker } from './SegmentMarkers';
import { ApproachProfileMarkers } from './ApproachProfileMarker';
import { axisId, convertSpeed, tracklogPointIndex } from 'ff/debrief/utils';
import { isFeatureEnabled } from 'ff/common/system';

const AltitudeSpeedChart = React.forwardRef<HTMLDivElement, {}>((_, ref) => {
    const app = useDebriefApp();
    const { isDecluttering } = app;
    const chrono = useChrono();
    const { currentTime, currentPoint } = chrono;
    const { points, status } = useCurrentTracklog();
    const { flightDataDirectory } = useFlightDataDirectory();
    const { selectedSegment, segmentTree } = useDebriefSegments();
    const { logAxisDataSelected } = useAnalytics();
    const { speedUnit } = useCurrentAircraft();

    const [leftAxis, setLeftAxis] = React.useState<AxisId>('altitude');
    const [rightAxis, setRightAxis] = React.useState<AxisId>('ground_speed');

    const dividerRef = React.useRef<HTMLDivElement>(null);

    const dataPoints = isDecluttering
        ? selectedSegment?.points || points?.data
        : points?.data;

    const isMphAxis = (axis: AxisId) => {
        return (
            ['airspeed', 'ground_speed'].includes(axis) && speedUnit === 'mph'
        );
    };

    const leftDataPoints = React.useMemo<[number[], number[]]>(() => {
        if (!status.isSuccess) return null;
        const sampler = getDownSampler(leftAxis);
        if (isDecluttering && selectedSegment?.isApproachSegment()) {
            const [distances, rawData] = sampler(
                dataPoints,
                tracklogPointIndex.distanceToRunway
            );
            const data = isMphAxis(leftAxis)
                ? rawData.map(value => convertSpeed(value, speedUnit))
                : rawData;
            return [distances, data];
        } else {
            const [timeStamps, rawData] = sampler(
                dataPoints,
                tracklogPointIndex.timestamp
            );
            const data = isMphAxis(leftAxis)
                ? rawData.map(value => convertSpeed(value, speedUnit))
                : rawData;
            return [timeStamps, data];
        }
    }, [
        status.isSuccess,
        selectedSegment?.id,
        isDecluttering,
        leftAxis,
        speedUnit,
    ]);

    const rightDataPoints = React.useMemo<[number[], number[]]>(() => {
        if (!status.isSuccess) return null;
        const [dates, rawData] = getDownSampler(rightAxis)(dataPoints);

        const data = isMphAxis(rightAxis)
            ? rawData.map(value => convertSpeed(value, speedUnit))
            : rawData;

        return [dates, data];
    }, [
        status.isSuccess,
        selectedSegment?.id,
        isDecluttering,
        rightAxis,
        speedUnit,
    ]);

    const terrainDataPoints = React.useMemo<[number[], number[]]>(() => {
        if (!status.isSuccess) return null;
        return getDownSampler(axisId.TerrainElevationAxis)(dataPoints);
    }, [status.isSuccess, selectedSegment?.id, isDecluttering, rightAxis]);

    // If the aircraft is configured to mph, replace the label
    const getAxisLabel = (axis: AxisId) =>
        isMphAxis(axis)
            ? axisConfig[axis].label.replace('kts', 'mph')
            : axisConfig[axis].label;

    const handleLeftAxisSelect = (selectedAxis: AxisId) => {
        logAxisDataSelected({
            axis: 'left',
            originalValue: axisConfig[leftAxis].label,
            newValue: axisConfig[selectedAxis].label,
        });

        if (selectedAxis === rightAxis) {
            setRightAxis(leftAxis);
        }
        setLeftAxis(selectedAxis);
    };

    const handleRightAxisSelect = (selectedAxis: AxisId) => {
        logAxisDataSelected({
            axis: 'right',
            originalValue: axisConfig[rightAxis].label,
            newValue: axisConfig[selectedAxis].label,
        });

        if (selectedAxis === leftAxis) {
            setLeftAxis(rightAxis);
        }
        setRightAxis(selectedAxis);
    };

    const handleScrubberClick = (xValue: number) => {
        // Check if we're in approach segment mode with distance-based x-axis
        const isDistanceBased =
            isDecluttering && selectedSegment?.isApproachSegment();

        chrono.setTimeFromXValue(xValue, {
            mode: isDistanceBased ? 'distance' : 'time',
            dataPoints: isDistanceBased ? dataPoints : undefined,
        });

        app.scrollSegmentIntoView();
    };

    // Calculate current X value based on mode (time or distance)
    const currentXValue = React.useMemo(() => {
        const isDistanceBased =
            isDecluttering && selectedSegment?.isApproachSegment();

        if (!isDistanceBased) {
            return currentTime;
        }

        // Find current point by timestamp and extract distance
        if (!dataPoints || dataPoints.length === 0) {
            return currentTime;
        }

        let closestPoint = null;
        let minDiff = Infinity;

        for (const point of dataPoints) {
            const pointTime = point[tracklogPointIndex.timestamp];
            const diff = Math.abs(pointTime - currentTime);

            if (diff < minDiff) {
                minDiff = diff;
                closestPoint = point;
            }
        }

        if (closestPoint) {
            return closestPoint[tracklogPointIndex.distanceToRunway];
        }

        return currentTime;
    }, [currentTime, isDecluttering, selectedSegment, dataPoints]);

    if (!points) return null;
    return (
        <Stack ref={ref} sx={{ position: 'relative', px: 1, height: 270 }}>
            <AxisSelectForm ref={dividerRef}>
                <AxisSelectField
                    selectedAxisId={leftAxis}
                    onAxisSelect={handleLeftAxisSelect}
                >
                    {flightDataDirectory.getPlottableFields().map(axis => (
                        <MenuItem key={axis} value={axis}>
                            <ColorBadge color={axisConfig[axis].color} />
                            {getAxisLabel(axis)}
                        </MenuItem>
                    ))}
                </AxisSelectField>
                <AxisSelectField
                    selectedAxisId={rightAxis}
                    onAxisSelect={handleRightAxisSelect}
                >
                    {flightDataDirectory.getPlottableFields().map(axis => (
                        <MenuItem key={axis} value={axis}>
                            <ColorBadge color={axisConfig[axis].color} />
                            {getAxisLabel(axis)}
                        </MenuItem>
                    ))}
                </AxisSelectField>
            </AxisSelectForm>
            <FlightChart
                xAxisData={leftDataPoints[0]}
                currentXValue={currentXValue}
                currentPoint={currentPoint}
                leftAxisId={leftAxis}
                rightAxisId={rightAxis}
                leftAxisData={leftDataPoints[1]}
                rightAxisData={rightDataPoints[1]}
                terrainData={terrainDataPoints[1]}
                tooltipBounds={dividerRef}
                speedUnit={speedUnit}
                onScrubberClick={handleScrubberClick}
            >
                {segmentTree &&
                    segmentTree
                        .getLeafNodes(
                            selectedSegment && {
                                start: leftDataPoints[0].at(0),
                                end: leftDataPoints[0].at(-1),
                            }
                        )
                        .map((segment, idx, arr) => {
                            const isApproach = segment.isApproachSegment();
                            return (
                                <SegmentMarker
                                    key={segment.id}
                                    selected={
                                        selectedSegment?.id === segment?.id ||
                                        segment?.hasParentMatching(
                                            parent =>
                                                selectedSegment?.id ===
                                                parent.id
                                        )
                                    }
                                    isOnlySelected={arr.length === 1}
                                    start={
                                        isApproach && isDecluttering
                                            ? segment.getHighestDistanceFromRunway()
                                            : segment.startTime
                                    }
                                    end={
                                        isApproach && isDecluttering
                                            ? segment.getLowestDistanceFromRunway()
                                            : segment.endTime
                                    }
                                    label={segment.label}
                                    color={segment.getColor()}
                                    leftPad={
                                        segment.hasScore(true)
                                            ? !arr.at(idx - 1)?.hasScore(true)
                                            : arr.at(idx - 1)?.hasScore(true)
                                    }
                                    rightPad={
                                        segment.hasScore(true)
                                            ? !arr.at(idx + 1)?.hasScore(true)
                                            : arr.at(idx + 1)?.hasScore(true)
                                    }
                                />
                            );
                        })}
                {isFeatureEnabled('debrief_iap') &&
                    selectedSegment?.isApproachSegment() &&
                    leftAxis === 'altitude' && (
                        <ApproachProfileMarkers
                            items={selectedSegment.getApproachProfileItems()}
                        />
                    )}
            </FlightChart>
        </Stack>
    );
});

AltitudeSpeedChart.displayName = 'AltitudeSpeedChart';

export default AltitudeSpeedChart;
